import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { kpiAdminService } from '../../services/kpiAdminService';
import { adminService } from '../../services/adminService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { KPISystemEditModal } from './KPISystemEditModal';

export function KPISystems() {
  const [systems, setSystems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingDeactivateId, setPendingDeactivateId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    country: '',
    version: '1.0',
  });

  const loadSystems = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      const data = await kpiAdminService.getKPISystems();
      if (isCancelled()) return;
      setSystems(data.filter(s => s.is_active));
    } catch (err) {
      if (isCancelled()) return;
      setError(mapSupabaseError(err));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(loadSystems, []);
  useAbortableFetch(async ({ isCancelled }) => {
    const data = await adminService.getAllCountries();
    if (!isCancelled()) setCountries(data);
  }, []);

  const handleCreate = () => {
    setEditingSystem(null);
    setFormData({ name: '', description: '', country: '', version: '1.0' });
    setShowForm(true);
  };

  const isCreateDirty =
    formData.name !== '' ||
    formData.description !== '' ||
    formData.country !== '' ||
    formData.version !== '1.0';

  const [discardCreateOpen, setDiscardCreateOpen] = useState(false);
  const attemptCloseCreate = () => {
    if (isCreateDirty) setDiscardCreateOpen(true);
    else setShowForm(false);
  };

  const handleEdit = (system) => {
    setEditingSystem(system);
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // handleEdit opens showEditModal (the dedicated edit modal), never showForm,
    // so this handler is only ever reached for the create flow.
    try {
      setError(null);
      await kpiAdminService.createKPISystem({
        ...formData,
        is_active: true,
        enabled_periods: ['Weekly', 'Monthly', 'Quarterly'],
      });
      toast.success('KPI system created');
      setShowForm(false);
      await loadSystems();
    } catch (err) {
      setError(mapSupabaseError(err));
    }
  };

  const handleDeactivate = (systemId) => {
    setPendingDeactivateId(systemId);
  };

  const confirmDeactivate = async () => {
    const systemId = pendingDeactivateId;
    setPendingDeactivateId(null);
    if (!systemId) return;
    try {
      setError(null);
      await kpiAdminService.deactivateKPISystem(systemId);
      toast.success('KPI system deactivated');
      await loadSystems();
    } catch (err) {
      setError(mapSupabaseError(err));
    }
  };

  const handleEditSystemSave = async (updatedSystemData) => {
    try {
      setError(null);
      await kpiAdminService.updateKPISystem(editingSystem.id, updatedSystemData);
      toast.success('System info updated');
      setShowEditModal(false);
      setEditingSystem(null);
      await loadSystems();
    } catch (err) {
      setError(mapSupabaseError(err));
    }
  };

  return (
    <div className="kpi-systems">
      <h2>KPI Systems</h2>
      {error && <div className="error-message" role="alert">{error}</div>}

      <button className="btn-primary" onClick={handleCreate}>
        Create New System
      </button>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="systems-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Country</th>
              <th>Description</th>
              <th>Version</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {systems.map(system => (
              <tr key={system.id}>
                <td>{system.name}</td>
                <td>{system.country || 'N/A'}</td>
                <td>{system.description || 'No description'}</td>
                <td>{system.version}</td>
                <td>
                  <button
                    className="btn-edit"
                    onClick={() => handleEdit(system)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => handleDeactivate(system.id)}
                  >
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <Modal onClose={attemptCloseCreate}>
          <h3>Create KPI System</h3>
          <form onSubmit={handleSubmit} noValidate>
            <label>
              System Name *
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                maxLength={100}
              />
            </label>
            <label>
              Country
              <select
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              >
                <option value="">Select a country…</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </label>
            <label>
              Description
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={500}
              />
            </label>
            <label>
              Version
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                pattern="^\d+(\.\d+){0,2}$"
                inputMode="decimal"
                title="Numeric only — e.g. 1, 1.0, or 1.0.0"
                maxLength={10}
              />
              <small>Numeric only — e.g. 1, 1.0, or 1.0.0</small>
            </label>
            <button type="submit" className="btn-primary">
              Create System
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={attemptCloseCreate}
            >
              Cancel
            </button>
          </form>
        </Modal>
      )}

      <KPISystemEditModal
        key={editingSystem?.id}
        system={editingSystem}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingSystem(null);
        }}
        onSave={handleEditSystemSave}
      />

      <ConfirmDialog
        isOpen={pendingDeactivateId !== null}
        title="Deactivate this KPI system?"
        message="The system will be hidden from active lists. You can reactivate it later from the database."
        confirmText="Deactivate"
        onConfirm={confirmDeactivate}
        onCancel={() => setPendingDeactivateId(null)}
      />

      <ConfirmDialog
        isOpen={discardCreateOpen}
        title="Discard new KPI system?"
        message="The values you've entered will be lost."
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={() => { setDiscardCreateOpen(false); setShowForm(false); }}
        onCancel={() => setDiscardCreateOpen(false)}
      />
    </div>
  );
}
